<template>
  <div class="h-full min-h-screen overflow-scroll bg-gray-900">
    <DSidebar
      expand-on-hover
      :menus="menus"
      :collapsible="collapse"
      :collapsed-width="collapsedWidth"
      :expanded-width="expandedWidth"
      @update:collapse="collapse = $event"
      :open-on-hover="false"
      icon-size="2xl"
      class="border-r border-gray-800 bg-[#1A1F2C] text-lg font-semibold text-gray-100">
      <template #head>
        <Workspace :display-full="!collapse" />
      </template>
      <!--Footer  -->
      <template #footer v-if="!collapse">
        <div
          class="inline-flex items-center justify-center border-t border-gray-800 p-4 text-sm font-normal text-gray-400 transition-all delay-200 duration-300 ease-in-out">
          &copy; 2025 Disguise.
        </div>
      </template>
    </DSidebar>
    <!-- Main centered container -->
    <div
      class="flex h-full min-h-[calc(100vh-80px)] flex-col items-start transition-all delay-100 duration-300 ease-in-out"
      :style="sidebarMargin">
      <Header />
      <div class="flex w-full flex-1 py-6">
        <slot />
      </div>
    </div>

    <!-- Create Workspace Dialog -->
  </div>
</template>
<script setup>
import { Header } from '@/components';
import { useAssetStore } from '@/stores/api/asset';
import { useWorkspaceStore } from '@/stores/api/workspace';
import { useUserStore } from '@/stores/userStore';
import { DSidebar } from '@disguise/cloud-ui';
import { computed, onMounted, ref, shallowRef, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import Spaces from './sidebar/Spaces.vue';
import Workspace from './sidebar/Workspace.vue';

// Store instances
const workspaceStore = useWorkspaceStore();
const assetStore = useAssetStore();
const userStore = useUserStore();

// Reactive data
const selectedWorkspaceId = ref(localStorage.getItem('crux-selected-workspace-id') || null);

const searchQuery = ref('');
const selectedAssetType = ref(null);
const workspacesLoading = ref(true);
const assetsLoading = ref(false);
const workspacesError = ref(null);
const assetsError = ref(null);

const router = useRouter();
const pagination = ref({
  page: 1,
  perPage: 6,
});
const collapse = ref(true);
const collapsedWidth = ref(60);
const expandedWidth = ref(200);

const sidebarMargin = computed(() => {
  const margin = collapse.value ? collapsedWidth.value : expandedWidth.value;
  return {
    marginLeft: `${margin}px`,
  };
});

const assets = computed(() => assetStore.assets || []);

const assetTypeOptions = computed(() => {
  const types = [...new Set(assets.value.map((asset) => asset.asset_type))];
  return types.map((type) => ({
    label: type.charAt(0).toUpperCase() + type.slice(1),
    value: type,
  }));
});

const filteredAssets = computed(() => {
  let filtered = [...assets.value];

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (asset) =>
        asset.latest_version?.name.toLowerCase().includes(query) || asset.asset_type.toLowerCase().includes(query)
    );
  }

  // Filter by asset type
  if (selectedAssetType.value) {
    filtered = filtered.filter((asset) => asset.asset_type === selectedAssetType.value);
  }

  // Pagination
  const start = (pagination.value.page - 1) * pagination.value.perPage;
  const end = start + pagination.value.perPage;
  return filtered.slice(start, end);
});

// Methods
const loadWorkspaces = async () => {
  // Check if user is authenticated before making API calls
  if (!userStore.isAuthenticated) {
    await userStore.loginWithProvider('oidc_auth0', '/callback');
    return;
  }

  workspacesLoading.value = true;
  workspacesError.value = null;
  try {
    await workspaceStore.fetch();
  } catch (error) {
    console.error('Error loading workspaces:', error);
    workspacesError.value = 'Failed to load workspaces. Please try again.';
  } finally {
    workspacesLoading.value = false;
  }
};

const onWorkspaceChange = async () => {
  if (!selectedWorkspaceId.value) return;

  assetsLoading.value = true;
  assetsError.value = null;
  try {
    await assetStore.fetchForWorkspace(selectedWorkspaceId.value);
  } catch (error) {
    console.error('Error loading assets:', error);
    assetsError.value = 'Failed to load assets. Please try again.';
  } finally {
    assetsLoading.value = false;
  }
};

const getAssetIcon = (type) => {
  const iconMap = {
    video: 'pi pi-video',
    image: 'pi pi-image',
    audio: 'pi pi-volume-up',
    document: 'pi pi-file',
    CX: 'pi pi-box',
    component: 'pi pi-cog',
    workspaceor: 'pi pi-video',
    led: 'pi pi-desktop',
    folder: 'pi pi-folder',
    default: 'pi pi-file',
  };
  return iconMap[type] || iconMap.default;
};

// Persist selected workspace to localStorage
watchEffect(() => {
  if (selectedWorkspaceId.value) {
    localStorage.setItem('crux-selected-workspace-id', selectedWorkspaceId.value);
  } else {
    localStorage.removeItem('crux-selected-workspace-id');
  }
});

// Helper function to handle workspace initialization after loading
const handleWorkspaceInitialization = async () => {
  await loadWorkspaces();

  // If we have a persisted workspace ID and workspaces are loaded, auto-load assets
  if (selectedWorkspaceId.value && workspaceStore.workspaces.length > 0) {
    // Verify the workspace still exists
    const workspaceExists = workspaceStore.workspaces.some((p) => p.code === selectedWorkspaceId.value);
    if (workspaceExists) {
      await onWorkspaceChange();
    } else {
      // Clear invalid workspace ID
      selectedWorkspaceId.value = null;
    }
  }
};

// Lifecycle
onMounted(async () => {
  // Wait for auth to be checked before attempting to load workspaces
  if (userStore.isLoading) {
    // Watch for loading to complete
    const unwatch = watch(
      () => userStore.isLoading,
      async (loading) => {
        if (!loading) {
          unwatch();
          await handleWorkspaceInitialization();
        }
      }
    );
  } else {
    // Auth has already finished loading
    await handleWorkspaceInitialization();
  }
});

const menus = ref([
  {
    label: 'Files',
    active: false,
    icon: 'pi pi-folder',
    component: shallowRef(Spaces),
  },
]);
</script>
